// Updated AdminSessionsListPage.tsx with proper TypeScript and error fixes

import { Autocomplete, TextField } from "@mui/material";
import PageTitle from "../../components/PageTitle";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import ArrowUpwardTwoToneIcon from "@mui/icons-material/ArrowUpwardTwoTone";
import ArrowDownwardTwoToneIcon from "@mui/icons-material/ArrowDownwardTwoTone";
import ArrowForwardTwoToneIcon from "@mui/icons-material/ArrowForwardTwoTone";
import { useEffect, useRef, useState } from "react";
import { PagedResults } from "../../types/PagedResults";
import { GetLiveAppStreamingSessionsParameters, LiveAppStreamingSession } from "../../types/Session";
import { useNavigate, useSearchParams } from "react-router-dom";
import useSessionsService from "../../services/SessionsService";
import useTitlesService from "../../services/TitlesService";
import PositiveButton from "../../components/PositiveButton";
import Ghost from "../../components/Ghost";
import useUserService from "../../services/UserService";

interface Title {
  id: string;
  name: string;
}

interface User {
  id: string;
  name?: string;
  email?: string;
}

export default function AdminLiveAppSessionsListPage() {
  const itemsPerPage = 10;

  const [selectedTitle, setSelectedTitle] =
    useState<Title | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [titleOptions, setTitleOptions] = useState<Title[]>([]);
  const [userOptions, setUserOptions] = useState<User[]>([]);
  const [titleMap, setTitleMap] = useState<
    Record<string, Title>
  >({});
  const [userMap, setUserMap] = useState<Record<string, User>>({});
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<PagedResults<LiveAppStreamingSession> | null>(
    null
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    titleId: searchParams.get("titleId") || "",
    userId: searchParams.get("userId") || "",
    page: parseInt(searchParams.get("page") || "1"),
    orderBy: searchParams.get("orderBy") || "started",
    orderDirection: searchParams.get("orderDirection") || "desc",
  });

  const handlePageChange = (newPage: number) => {
    setFormData((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const { searchLiveAppStreamingSessions } = useSessionsService();
  const { searchTitles, getTitle } = useTitlesService();
  const { searchUsers, getUser } = useUserService();

  const debounceDelay = 500;
  const debounceTimers = useRef<Record<string, NodeJS.Timeout>>({});

  useEffect(() => {
    if (formData.titleId) {
      getTitle(formData.titleId).then(
        (title) => {
          if (title) {
            setTitleOptions([title]);
            setSelectedTitle(title);
          }
        }
      );
    }
  }, [formData.titleId]);

  useEffect(() => {
    if (formData.userId) {
      getUser(formData.userId).then((user) => {
        if (user) {
          setUserOptions([user]);
          setSelectedUser(user);
        }
      });
    }
  }, [formData.userId]);

  const formatDate = (dateStr: string) =>
    new Intl.DateTimeFormat(navigator.language, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(dateStr));

  useEffect(() => {
    const handler = setTimeout(() => {
      search();
    }, debounceDelay);
    return () => clearTimeout(handler);
  }, [
    formData.titleId,
    formData.userId,
    formData.page,
    formData.orderBy,
    formData.orderDirection,
  ]);

  const search = () => {
    setIsSearching(true);
    setSearchParams({
      titleId: formData.titleId,
      userId: formData.userId,
      page: formData.page.toString(),
      orderBy: formData.orderBy,
      orderDirection: formData.orderDirection,
    });

    const request: GetLiveAppStreamingSessionsParameters = {
      titleId: formData.titleId || undefined,
      userId: formData.userId || undefined,
      page: formData.page,
      orderBy: formData.orderBy,
      orderDirection: formData.orderDirection,
      itemsPerPage,
    };

    searchLiveAppStreamingSessions(request)
      .then(async (res) => {
        setResults(res);

        const uniqueTitleIds = Array.from(
          new Set(
            res.items
              .filter((x) => x.titleId != null && x.titleId !== "" && x.titleId !== undefined)
              .map((x) => x.titleId)
              .filter((id) => id && !titleMap[id])
          )
        );

        const uniqueUserIds = Array.from(
          new Set(
            res.items.map((x) => x.userId).filter((id) => id && !userMap[id])
          )
        );

        const newTitles = await Promise.all(
          uniqueTitleIds.map((id) =>
            getTitle(id!).catch(() => null)
          )
        );
        const newUsers = await Promise.all(
          uniqueUserIds.map((id) => getUser(id!).catch(() => null))
        );

        setTitleMap((prev) => ({
          ...prev,
          ...Object.fromEntries(
            newTitles.filter(Boolean).map((x) => [x!.id, x!])
          ),
        }));
        setUserMap((prev) => ({
          ...prev,
          ...Object.fromEntries(
            newUsers.filter(Boolean).map((x) => [x!.id, x!])
          ),
        }));
      })
      .finally(() => setIsSearching(false));
  };

  const handleSort = (column: string) => {
    setFormData((prev) => ({
      ...prev,
      orderBy: column,
      orderDirection:
        prev.orderBy === column && prev.orderDirection === "asc"
          ? "desc"
          : "asc",
      page: 1,
    }));
  };

  return (
    <div className="flex flex-col gap-4">
      <PageTitle>
        <LiveTvIcon /> BeamXR Live App Sessions
      </PageTitle>

      <div className="card p-4 flex flex-col gap-4">
        <h2 className="text-lg">Filters</h2>
        <div className="grid grid-cols-2 gap-4">
          <Autocomplete
            value={selectedTitle}
            onChange={(_, val) => {
              setSelectedTitle(val);
              setFormData((prev) => ({
                ...prev,
                titleId: val?.id || "",
                page: 1,
              }));
            }}
            onInputChange={(_, val) => {
              clearTimeout(debounceTimers.current.title);
              debounceTimers.current.title = setTimeout(() => {
                searchTitles(val).then((r) =>
                  setTitleOptions(r.items || [])
                );
              }, debounceDelay);
            }}
            getOptionLabel={(option) => option.name || ""}
            options={titleOptions}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by Title"
                variant="outlined"
                sx={{
                  backgroundColor: "white",
                  borderRadius: 1,
                  '& label.Mui-focused': {
                    color: 'black',
                    backgroundColor: 'white',
                    padding: '0 4px',
                  },
                  '& .MuiInputLabel-shrink': {
                    backgroundColor: 'white',
                    padding: '0 4px',
                  }}}
              />
            )}
          />

          <Autocomplete
            value={selectedUser}
            onChange={(_, val) => {
              setSelectedUser(val);
              setFormData((prev) => ({
                ...prev,
                userId: val?.id || "",
                page: 1,
              }));
            }}
            onInputChange={(_, val) => {
              clearTimeout(debounceTimers.current.user);
              debounceTimers.current.user = setTimeout(() => {
                searchUsers(val).then((r) => setUserOptions(r.items || []));
              }, debounceDelay);
            }}
            getOptionLabel={(option) => option.name || option.email || ""}
            options={userOptions}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by User"
                variant="outlined"
                sx={{
                  backgroundColor: "white",
                  borderRadius: 1,
                  '& label.Mui-focused': {
                    color: 'black',
                    backgroundColor: 'white',
                    padding: '0 4px',
                  },
                  '& .MuiInputLabel-shrink': {
                    backgroundColor: 'white',
                    padding: '0 4px',
                  }}}
              />
            )}
          />
        </div>
      </div>

      <section role="content">
        <div className="flex flex-col gap-4">
          <table className="table-auto w-full card border-spacing-2">
            <thead>
              <tr>
                <th
                  className="text-left p-4 cursor-pointer"
                  onClick={() => handleSort("started")}
                >
                  Started{" "}
                  {formData.orderBy === "started" &&
                    (formData.orderDirection === "asc" ? (
                      <ArrowUpwardTwoToneIcon />
                    ) : (
                      <ArrowDownwardTwoToneIcon />
                    ))}
                </th>
                <th className="text-left p-4">Title</th>
                <th className="text-left p-4">User</th>
                <th className="text-left p-4"></th>
              </tr>
            </thead>
            <tbody>
              {isSearching &&
                Array.from({ length: itemsPerPage }).map((_, i) => (
                  <tr key={i} className="border-t border-cyan-900">
                    <td className="p-4">
                      <Ghost additionalClasses="p-4 rounded" />
                    </td>
                    <td className="p-4">
                      <Ghost additionalClasses="p-4 rounded" />
                    </td>
                    <td className="p-4">
                      <Ghost additionalClasses="p-4 rounded" />
                    </td>
                    <td className="p-4 text-right">
                      <ArrowForwardTwoToneIcon />
                    </td>
                  </tr>
                ))}
              {!isSearching &&
                results?.items.map((s) => (
                  <tr
                    key={s.id}
                    className="border-t border-cyan-900 hover:bg-cyan-300/25 cursor-pointer"
                    onClick={() => navigate(`/admin/sessions/liveapp/${s.userId}/${s.id}`)}
                  >
                    <td className="p-4">{formatDate(s.startedStamp)}</td>
                    <td className="p-4">
                      <span className="inline-flex items-center gap-2">
                        {s.titleId ? titleMap[s.titleId!]?.name || (
                          <Ghost additionalClasses="p-4 rounded" />
                        ): "*No Title - just a session*"}
                        {s.hasMultipleTitles && (
                          <span className="rounded-full bg-cyan-700 px-2 py-0.5 text-xs whitespace-nowrap">
                            Multiple titles
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="p-4">
                      {userMap[s.userId!]?.email || (
                        <Ghost additionalClasses="p-4 rounded" />
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <ArrowForwardTwoToneIcon />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Pagination Section */}
      {results && results.totalPages > 1 && (
        <section className="flex justify-between items-center mt-4">
          <PositiveButton
            onClick={() => handlePageChange(formData.page - 1)}
            disabled={formData.page === 1}
          >
            Previous
          </PositiveButton>
          <span>
            Page {formData.page} of {results.totalPages}
          </span>
          <PositiveButton
            onClick={() => handlePageChange(formData.page + 1)}
            disabled={formData.page === results.totalPages}
          >
            Next
          </PositiveButton>
        </section>
      )}
    </div>
  );
}
